/**
 * Review Card Component for Customer Testimonials
 * 
 * A comprehensive customer review display component that showcases authentic
 * customer feedback with photos, ratings, and detailed review content.
 * Features modal image viewing and robust image handling.
 * 
 * Display Features:
 * - Customer profile information (name, location)
 * - Star rating visualization (1-5 stars)
 * - Bike model and size information
 * - Review text with proper typography
 * - Customer photo gallery with modal viewing
 * 
 * Image Handling:
 * - Multiple image format support (URLs, Sanity images)
 * - Robust fallback system for missing images
 * - Modal popup for full-size image viewing
 * - Responsive thumbnail grid layout
 * - Error handling for incomplete image data
 * 
 * Visual Design:
 * - Clean card layout with subtle shadows
 * - Professional typography hierarchy
 * - Responsive grid system for image galleries
 * - Smooth hover animations and transitions
 * - Mobile-optimized touch interactions
 * 
 * User Experience:
 * - Click-to-expand image functionality
 * - Escape key and overlay click to close modal
 * - Smooth modal transitions with backdrop
 * - Accessible image descriptions and alt text
 * - Loading states for dynamic content
 * 
 * Technical Implementation:
 * - React state management for modal visibility
 * - Flexible image URL processing for multiple formats
 * - Event handling for modal interactions
 * - Conditional rendering for optional content
 * - Performance-optimized image loading
 * 
 * Accessibility:
 * - Semantic HTML structure for review content
 * - Keyboard navigation for image modal
 * - Screen reader compatible rating display
 * - High contrast text for readability
 * - Focus management for modal interactions
 * 
 * @param {string} firstName - Customer's first name
 * @param {string} lastName - Customer's last name
 * @param {string} country - Customer's country/location
 * @param {string} bikeModel - Bike model being reviewed
 * @param {string} bikeSize - Size of the bike purchased
 * @param {number} rating - Customer rating (1-5 stars, default: 5)
 * @param {string} reviewText - Customer's review content
 * @param {Array} images - Array of customer photos (URLs or Sanity images)
 * 
 * @description Customer review display with image gallery and modal viewing
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { X } from 'lucide-react';

import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';

const ReviewCard = ({ 
  firstName,  
  lastName,
  country, 
  bikeModel,
  bikeSize,
  rating = 5, 
  reviewText,
  images = [],
  onImageClick,
  onImageClose,
  isModalIndependent = false
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [mounted, setMounted] = useState(false);
  const customerName = `${firstName} ${lastName}`;
  const bikeType = bikeSize ? `${bikeModel} ${bikeSize}` : bikeModel;

  // Ensure component is mounted (for portal)
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle image click with callback
  const handleImageClick = (image) => {
    setSelectedImage(image);
    if (onImageClick) {
      onImageClick();
    }
  };

  // Handle image close with callback
  const handleImageClose = () => {
    setSelectedImage(null);
    if (onImageClose) {
      onImageClose();
    }
  };

  // Convert country abbreviation to full name for display
  const getCountryDisplayName = (countryCode) => {
    const countryMap = {
      'FR': 'France',
      'CH': 'Switzerland', 
      'DE': 'Germany',
      'IT': 'Italy',
      'UK': 'United Kingdom',
      'US': 'United States'
    };
    return countryMap[countryCode] || countryCode;
  };

  // Helper function to get the image URL (handles both string URLs and Sanity images)
  const getImageUrl = (image) => {
    if (!image) return null;
    
    // Handle string URLs directly
    if (typeof image === 'string') return image;
    
    // Handle Sanity images with proper asset references
    if (image.asset && (image.asset._ref || image.asset._id)) {
      return urlForImage(image);
    }
    
    // Handle incomplete Sanity image objects (missing asset reference)
    if (image._type === 'image') {
      console.warn('Incomplete Sanity image object:', image);
      return '/images/fallback-user.jpg'; // Fallback image for customer photos
    }
    
    // Last resort - try urlForImage anyway
    return urlForImage(image);
  };

  return (
    <>
      <div className="flex-shrink-0 w-full sm:w-[350px] p-6 bg-white rounded-lg shadow-md border border-gray-100">
        <div className="flex items-center mb-4">
          {/* Customer Profile */}
          <div>
            <h4 className="font-bold">{customerName}</h4>
            <p className="text-sm text-gray-600">{getCountryDisplayName(country)}</p>
          </div>
        </div>
        
        <p className="text-sm mb-2">{bikeType}</p>
        
        {/* Star Rating */}
        <div className="flex mb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <svg 
              key={i} 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 24 24" 
              fill={i < rating ? 'currentColor' : 'none'} 
              stroke="currentColor"
              className={`w-5 h-5 ${i < rating ? 'text-black' : 'text-gray-300'}`}
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={i < rating ? 0 : 2} 
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" 
              />
            </svg>
          ))}
        </div>
        
        {/* Review Text */}
        <p className="text-gray-700 mb-4">{reviewText}</p>
        
        {/* Review Images */}
        {images && images.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {images.map((image, index) => {
              const imageUrl = getImageUrl(image);
              if (!imageUrl) return null;
              
              return (
                <div 
                  key={index}
                  className="w-16 h-16 relative rounded-md overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => handleImageClick(image)}
                >
                  <Image
                    src={imageUrl}
                    alt={`Review image ${index + 1}`}
                    fill
                    unoptimized
                    className="object-cover"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Lightbox Modal - Rendered via Portal for independence */}
      {selectedImage && mounted && isModalIndependent ? (
        createPortal(
          <div 
            className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
            onClick={handleImageClose}
            style={{ margin: 0 }}
          >
            <div className="relative max-w-4xl w-full">
              {/* Close button positioned outside and above the image */}
              <button 
                className="absolute -top-12 right-0 bg-white rounded-full p-2 text-black z-10 hover:bg-gray-100 transition-colors shadow-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClose();
                }}
              >
                <X size={24} />
              </button>
              
              {/* Image container */}
              <div className="relative w-full h-[80vh] bg-black/20 rounded-lg overflow-hidden">
                <Image
                  src={getImageUrl(selectedImage)}
                  alt="Review image"
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 768px) 100vw, 800px"
                  className="object-contain"
                />
              </div>
            </div>
          </div>,
          document.body
        )
      ) : selectedImage && !isModalIndependent ? (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={handleImageClose}
        >
          <div className="relative max-w-4xl w-full">
            {/* Close button positioned outside and above the image */}
            <button 
              className="absolute -top-12 right-0 bg-white rounded-full p-2 text-black z-10 hover:bg-gray-100 transition-colors shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                handleImageClose();
              }}
            >
              <X size={24} />
            </button>
            
            {/* Image container */}
            <div className="relative w-full h-[80vh] bg-black/20 rounded-lg overflow-hidden">
              <Image
                src={getImageUrl(selectedImage)}
                alt="Review image"
                fill
                loader={sanityLoader}
                className="object-contain"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ReviewCard; 